import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = {
    key: 1,
    class: "flex flex-col items-start gap-3 rounded-(--border-border-radius-rounded-sm) border border-(--colors-border-error-subtle) bg-(--colors-background-error) px-4 py-3",
    role: "alert",
    "data-testid": "consent-history-error"
};
const _hoisted_2 = { class: "text-sm text-(--colors-text-error-strong)" };
const _hoisted_3 = {
    key: 2,
    class: "text-sm text-(--colors-text-body-subtle)",
    "data-testid": "consent-history-empty"
};
const _hoisted_4 = ["aria-label"];
const _hoisted_5 = {
    key: 0,
    class: "mb-2.5 w-full border-0 border-t border-(--border-border-base-soft)",
    "aria-hidden": "true"
};
const _hoisted_6 = { class: "flex list-none flex-wrap items-center gap-1.5 p-0" };
const _hoisted_7 = { class: "sr-only" };
const _hoisted_8 = { class: "flex flex-col gap-1 rounded-(--border-border-radius-rounded-sm) border border-(--border-border-base-soft) bg-(--colors-background-white-secondary-soft) px-4 py-3" };
const _hoisted_9 = {
    class: "flex items-center gap-1.5 text-xs leading-5 font-medium text-(--text-text-heading)",
    "data-testid": "consent-history-author"
};
const _hoisted_10 = ["href", "aria-label"];
const _hoisted_11 = ["title"];
const _hoisted_12 = {
    class: "text-xs leading-5 font-normal text-(--text-text-body-subtle)",
    "data-testid": "consent-history-comment"
};
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Badge, Button, Drawer, Dropdown } from "@eudonet/opal";
import { CONSENT_STATUS_ICON, CONSENT_STATUS_THEME, } from "@/modules/consent/domain/entities/consentOverview";
import { formatDateTime } from "@/shared/utils/date-formatting";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import { useProfileStore } from "@/modules/settings/public";
/**
 * Consent history for ONE communication address, as a right-hand drawer.
 *
 * One list item = one MODIFICATION, never one finalité: the backend collapses an operator
 * declaration's N purpose rows into a single entry carrying every change plus the justification
 * recorded once. Rendering them per-purpose is exactly what the PO rejected.
 *
 * Composed entirely from OPAL primitives — `Drawer` (`size="md"` is the 320px the design asks for),
 * `Badge`, `Dropdown` + `Button`. The list item anatomy itself (timestamp badge, a
 * wrapping badge row, a bordered justification block) has no OPAL equivalent, so it is page-specific
 * layout over DS tokens.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'ConsentHistoryDrawer',
    props: {
        modelValue: { type: Boolean },
        address: {},
        entries: {},
        loading: { type: Boolean },
        hasError: { type: Boolean },
        purposeOptions: {},
        selectedPurpose: {}
    },
    emits: ["update:modelValue", "update:selectedPurpose", "retry"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const profileStore = useProfileStore();
        const isOpen = computed({
            get: () => props.modelValue,
            set: (value) => emit("update:modelValue", value),
        });
        // The design writes the stamp as "10/08/2026 10:32" — a numeric date, so it follows the profile
        // format (R30) rather than a hardcoded pattern, with the time appended as the design shows it.
        function formatStamp(iso) {
            return formatDateTime(iso, locale.value, profileStore.profile?.dateFormat, profileStore.profile?.timeFormat);
        }
        const allPurposesLabel = computed(() => t("contact.detail.consents.historyDrawer.allPurposes"));
        const activeFilterLabel = computed(() => {
            if (!props.selectedPurpose)
                return allPurposesLabel.value;
            return (props.purposeOptions.find((option) => option.code === props.selectedPurpose)?.label ??
                allPurposesLabel.value);
        });
        const filterItems = computed(() => [
            {
                label: allPurposesLabel.value,
                active: props.selectedPurpose === null,
                testId: "consent-history-filter-all",
                onClick: () => emit("update:selectedPurpose", null),
            },
            ...props.purposeOptions.map((option) => ({
                label: option.label,
                active: props.selectedPurpose === option.code,
                testId: `consent-history-filter-${option.code}`,
                onClick: () => emit("update:selectedPurpose", option.code),
            })),
        ]);
        /**
         * Purpose changes ordered by their LOCALISED label. The backend orders by purpose code so the wire
         * shape is stable; what a French reader should see is alphabetical in French, and the operator's own
         * input order is not recoverable (a declaration's rows share one instant and carry random ids).
         */
        function orderedChanges(entry) {
            return [...entry.purposeChanges].sort((a, b) => purposeLabel(a.purpose).localeCompare(purposeLabel(b.purpose), locale.value));
        }
        function purposeLabel(code) {
            return props.purposeOptions.find((option) => option.code === code)?.label ?? code;
        }
        /**
         * "Michel Lebrun · Formulaire" for an operator declaration.
         *
         * An automated write has neither an author nor a collection mode, and the design never draws that
         * case: it falls back to a localised "Système" plus the technical source, so the line always states
         * WHO and HOW — never a blank, and never a backend-supplied French constant (R76).
         */
        function authorLine(entry) {
            const author = entry.authorName ?? t("contact.detail.consents.historyDrawer.systemAuthor");
            const origin = entry.collectionMode
                ? t(`contact.detail.consents.collectionMode.${entry.collectionMode}`)
                : sourceLabel(entry.source);
            return `${author} · ${origin}`;
        }
        function sourceLabel(source) {
            const key = `contact.detail.consents.source.${source}`;
            // `te` is not used here: the catch-all is the raw source, which is a stable backend enum name and
            // still readable by an auditor if a locale key is ever missing.
            const label = t(key);
            return label === key ? source : label;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Drawer), {
                modelValue: isOpen.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((isOpen).value = $event)),
                position: "right",
                size: "md",
                title: _unref(t)('contact.detail.consents.historyDrawer.title'),
                subtitle: __props.address,
                icon: "ri-history-line",
                "aria-close-label": _unref(t)('common.close')
            }, {
                subheader: _withCtx(() => [
                    _createVNode(_unref(Dropdown), {
                        items: filterItems.value,
                        "menu-align": "left",
                        "selection-style": "check",
                        "menu-header": _unref(t)('contact.detail.consents.historyDrawer.filterBy'),
                        "aria-label": _unref(t)('contact.detail.consents.historyDrawer.filterAria'),
                        "data-testid": "consent-history-filter"
                    }, {
                        trigger: _withCtx(({ triggerProps }) => [
                            _createVNode(_unref(Button), _mergeProps(triggerProps, {
                                variant: "secondary",
                                size: "xs",
                                "icon-left": "ri-filter-3-line",
                                "dropdown-icon": "",
                                label: activeFilterLabel.value,
                                "data-testid": "consent-history-filter-trigger"
                            }), null, 16 /* FULL_PROPS */, ["label"])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["items", "menu-header", "aria-label"])
                ]),
                default: _withCtx(() => [
                    (__props.loading)
                        ? (_openBlock(), _createBlock(ModuleLoading, {
                            key: 0,
                            "data-testid": "consent-history-loading"
                        }))
                        : (__props.hasError)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)("contact.detail.consents.historyDrawer.loadError")), 1 /* TEXT */),
                                _createVNode(_unref(Button), {
                                    variant: "secondary",
                                    size: "xs",
                                    "icon-left": "ri-refresh-line",
                                    label: _unref(t)('contact.detail.consents.retry'),
                                    "data-testid": "consent-history-retry",
                                    onClick: _cache[0] || (_cache[0] = ($event) => (emit('retry')))
                                }, null, 8 /* PROPS */, ["label"])
                            ]))
                            : (__props.entries.length === 0)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_unref(t)("contact.detail.consents.historyDrawer.empty")), 1 /* TEXT */))
                                : (_openBlock(), _createElementBlock("ul", {
                                    key: 3,
                                    class: "flex list-none flex-col gap-4 p-0",
                                    "aria-label": _unref(t)('contact.detail.consents.historyDrawer.title'),
                                    "data-testid": "consent-history-list"
                                }, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.entries, (entry, index) => {
                                        return (_openBlock(), _createElementBlock("li", {
                                            key: entry.entryId,
                                            class: "flex flex-col gap-1.5",
                                            "data-testid": "consent-history-entry"
                                        }, [
                                            (index > 0)
                                                ? (_openBlock(), _createElementBlock("hr", _hoisted_5))
                                                : _createCommentVNode("v-if", true),
                                            _createVNode(_unref(Badge), {
                                                type: "with-dot",
                                                theme: "dark",
                                                size: "sm",
                                                label: formatStamp(entry.occurredAt),
                                                "show-left-icon": false,
                                                "data-testid": "consent-history-stamp"
                                            }, null, 8 /* PROPS */, ["label"]),
                                            _createElementVNode("ul", _hoisted_6, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(orderedChanges(entry), (change) => {
                                                    return (_openBlock(), _createElementBlock("li", {
                                                        key: change.purpose
                                                    }, [
                                                        _createVNode(_unref(Badge), {
                                                            label: purposeLabel(change.purpose),
                                                            theme: _unref(CONSENT_STATUS_THEME)[change.newStatus],
                                                            size: "xs",
                                                            "show-close-icon": false,
                                                            "data-testid": "consent-history-purpose",
                                                            "left-icon": _unref(CONSENT_STATUS_ICON)[change.newStatus]
                                                        }, null, 8 /* PROPS */, ["label", "theme", "left-icon"]),
                                                        _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)(`contact.detail.consents.status.${change.newStatus}`)), 1 /* TEXT */)
                                                    ]));
                                                }), 128 /* KEYED_FRAGMENT */))
                                            ]),
                                            _createElementVNode("div", _hoisted_8, [
                                                _createElementVNode("p", _hoisted_9, [
                                                    _cache[2] || (_cache[2] = _createElementVNode("i", {
                                                        class: "ri-user-line text-xs",
                                                        "aria-hidden": "true"
                                                    }, null, -1 /* CACHED */)),
                                                    _createTextVNode(" " + _toDisplayString(authorLine(entry)), 1 /* TEXT */)
                                                ]),
                                                (entry.proof)
                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                        (entry.proof.downloadUrl)
                                                            ? (_openBlock(), _createElementBlock("a", {
                                                                key: 0,
                                                                href: entry.proof.downloadUrl,
                                                                target: "_blank",
                                                                rel: "noopener noreferrer",
                                                                class: "flex items-center gap-1.5 text-xs leading-5 font-medium text-(--text-text-link) underline-offset-2 hover:underline focus-visible:underline",
                                                                "aria-label": _unref(t)('contact.detail.consents.historyDrawer.proofAria', { fileName: entry.proof.fileName }),
                                                                "data-testid": "consent-history-proof-link"
                                                            }, [
                                                                _cache[3] || (_cache[3] = _createElementVNode("i", {
                                                                    class: "ri-attachment-2 text-xs",
                                                                    "aria-hidden": "true"
                                                                }, null, -1 /* CACHED */)),
                                                                _createTextVNode(" " + _toDisplayString(entry.proof.fileName), 1 /* TEXT */)
                                                            ], 8 /* PROPS */, _hoisted_10))
                                                            : (_openBlock(), _createElementBlock("p", {
                                                                key: 1,
                                                                class: "flex items-center gap-1.5 text-xs leading-5 font-medium text-(--colors-text-body-subtle)",
                                                                title: _unref(t)('contact.detail.consents.historyDrawer.proofUnavailable'),
                                                                "data-testid": "consent-history-proof-unavailable"
                                                            }, [
                                                                _cache[4] || (_cache[4] = _createElementVNode("i", {
                                                                    class: "ri-attachment-2 text-xs",
                                                                    "aria-hidden": "true"
                                                                }, null, -1 /* CACHED */)),
                                                                _createTextVNode(" " + _toDisplayString(entry.proof.fileName), 1 /* TEXT */)
                                                            ], 8 /* PROPS */, _hoisted_11))
                                                    ], 64 /* STABLE_FRAGMENT */))
                                                    : _createCommentVNode("v-if", true),
                                                _createElementVNode("p", _hoisted_12, _toDisplayString(entry.comment || _unref(t)("contact.detail.consents.historyDrawer.noComment")), 1 /* TEXT */)
                                            ])
                                        ]));
                                    }), 128 /* KEYED_FRAGMENT */))
                                ], 8 /* PROPS */, _hoisted_4))
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "title", "subtitle", "aria-close-label"]));
        };
    }
});
